import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router';
import { LanguagesAbbr, PageModule, PageModuleType, Session, SessionPreview } from '../../../../../types/working-model';
import ModalComponent from '../../../../general-ui/modal/modal';
import './add-to-calendar-modal.scss';

interface IAddToCalendarModal {
	open: boolean;
	close: () => void;
	session: Session | SessionPreview;
	className?: string;
	language: LanguagesAbbr;
	isSingleSessionNoHomepage?: boolean;
}

const AddToCalendarModal: React.FC<IAddToCalendarModal> = (props) => {
	const {
		open,
		close,
		session,
		className,
		language,
		isSingleSessionNoHomepage = false,
	} = props;

	const { t } = useTranslation("session");
	const { pathname } = useLocation();
	const match = useRouteMatch();
	function pad(i: number) {
		return i.toString().padStart(2, '0');
	}

	const sessionDescription = useMemo(() => {
		if (!hasFullSessionProperties(session)) return null;
		// find the first session description module and use that if it is_on

		const descriptionModule = session?.modules?.find((module: PageModule) => module.type === PageModuleType.description && module.is_on);
		// if the description modules description has changed, then use it, otherwise we arent going to include a description in the calendar event
		if (descriptionModule && ('changed' in descriptionModule?.content?.description)) {
			return descriptionModule?.content?.description?.[language] ?? descriptionModule?.content?.description?.base;
		}
		return null;
	}, [language, session]);

	function convertDate(timestamp: number | null) {
		if (timestamp) {
			// format the time stamp correctly
			const event = new Date(timestamp);
			const year = event.getUTCFullYear();
			const month = pad(event.getUTCMonth() + 1);
			const day = pad(event.getUTCDate());
			const hour = pad(event.getUTCHours());
			const minute = pad(event.getUTCMinutes());
			const second = pad(event.getUTCSeconds());
			return `${year}${month}${day}T${hour}${minute}${second}Z`;
		}
	}

	function getLiveEventSessionUrl(url: string) {
		// custom domain names live in super admin
		const base = url.split('/')[2];

		let pathToSession;

		// if add to calendar on single session without homepage
		if (isSingleSessionNoHomepage) {
			pathToSession = pathname;
			// if add to calendar from a session
		} else if (match.path.includes('sessionUuid')) {
			// if add current session to calendar
			if (pathname.includes(session.uuid)) {
				pathToSession = pathname;
			} else {
				// if add to calendar on similar session module, 
				// replace current session uuid with the similar session uuid
				const pathAsArray = pathname.split('/');
				pathAsArray.splice(-1, 1, session.uuid);
				pathToSession = pathAsArray.join('/');
			}
		} else if (match.path.endsWith('language')) {
			// add to calendar from LP
			pathToSession = `${pathname}/session/${session.uuid}`;
		} else {
			// add to calendar everywhere after registration
			const pathAsArray = pathname.split('/');
			pathAsArray.splice(-1);
			pathToSession = `${pathAsArray.join('/')}/session/${session.uuid}`;
		}
		const path = base + pathToSession;
		return path;
	}

	function makeIcsFileLink() {
		const isDevelopment = process.env.NODE_ENV === 'development';
		const icsFileText =
			'BEGIN:VCALENDAR\n' +
			'PRODID:-//Brandlive//NONSGML HandCal//EN' +
			'VERSION:2.0\n' +
			'BEGIN:VEVENT\n' +
			'UID:' + session.uuid + '\n' +
			'DTSTART:' +
			convertDate(session?.timestamp) +
			'\n' +
			'DTEND:' +
			convertDate(session?.end_timestamp) +
			'\n' +
			'SUMMARY:' +
			(session?.title?.[language] ?? session?.title?.base) +
			'\n' +
			'DESCRIPTION:' +
			(sessionDescription ? sessionDescription : '') +
			'\n' +
			`URL:${isDevelopment ? 'http' : 'https'}://` + getLiveEventSessionUrl(window.location.href) +
			'\n' +
			'END:VEVENT\n' +
			'END:VCALENDAR'
			;

		const data = new File([icsFileText], session.title.base.replace(/[^0-9a-zA-Z]/g, '-'), { type: 'text/calendar' });

		return window.URL.createObjectURL(data);
	}

	function renderIcsLink(provider: string, idx: number) {
		return (
			<a key={idx} onClick={close} className="no-style add-to-calendar" download={`${session.title.base.replace(/[^0-9a-zA-Z]/g, '-')}-calendar-invite.ics`} href={makeIcsFileLink()}>{provider}</a>
		);
	}
	function renderGoogleCalLink(session: Session | SessionPreview) {
		const isDevelopment = process.env.NODE_ENV === 'development';

		const descriptionAsUrl = (sessionDescription ? encodeURIComponent(sessionDescription) : '') + (isDevelopment ? '+http://' : '+https://') + getLiveEventSessionUrl(window.location.href);

		const titleAsUrl = encodeURIComponent((session?.title?.[language] as string | undefined) ?? session?.title?.base);

		return <a onClick={close} key={session.timestamp} className="no-style add-to-calendar" target="_blank" rel="noreferrer" href={`https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${convertDate(session?.timestamp)}/${convertDate(session?.end_timestamp)}&text=${titleAsUrl}&details=${descriptionAsUrl}`}>Google</a>;
	}

	return (
		<ModalComponent
			size={'small'}
			title={t("Add to Calendar")}
			open={open}
			onRequestClose={close}
			closeable={false}
			cancellable
			footer={<>
				<button onClick={close}>{t("Cancel")}</button>
				<button className={"primary"} onClick={close}>{t("Done")}</button>
			</>}
		>
			<div className={classNames("add-to-calendar-modal", className)}>
				{
					['iCal', 'Outlook'].map((provider, idx) => renderIcsLink(provider, idx))
				}
				{renderGoogleCalLink(session)}
			</div>
		</ModalComponent>
	);
};

function hasFullSessionProperties(session: Session | SessionPreview): session is Session {
	return (session as Session)?.modules !== undefined;
}

export default AddToCalendarModal;
